import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

/**
 * One Card primitive, three sizes — picks the right border-radius, shadow,
 * and background to enforce a consistent visual hierarchy across all
 * screens. Before this existed, every screen rolled its own combination of
 * `rounded-2xl` / `rounded-3xl` / `shadow-brand` / `shadow-brand-lg`,
 * which is exactly the kind of drift that makes a UI feel inconsistent.
 *
 * Hierarchy:
 *  - `hero`    — the single most important card on a screen. Bigger radius,
 *                heavier shadow. Use for hero score, primary CTAs, the
 *                "this is what this screen is about" surface.
 *  - `default` — every other card. Slightly smaller radius, lighter shadow.
 *                Use for content sections (transcript, criteria, etc.).
 *  - `flat`    — no shadow, no surface fill. Use when you only need the
 *                rounded border (e.g. a tinted info banner).
 *
 * Tones map to a visual identity:
 *  - `surface`     — neutral white card with brand border. Workhorse.
 *  - `gradient`    — brand-gradient-hero with white text. The signature
 *                    look used on Home modules; use sparingly elsewhere
 *                    (one per screen) so it stays "the hero card".
 *  - `*-soft`      — tinted background for status callouts (info, warning,
 *                    error) and feedback callouts (primary).
 */

export type CardSize = 'hero' | 'default' | 'flat'
export type CardTone =
  | 'surface'
  | 'gradient'
  | 'primary-soft'
  | 'warning-soft'
  | 'error-soft'
  | 'info-soft'

interface CardProps extends HTMLAttributes<HTMLElement> {
  size?: CardSize
  tone?: CardTone
  /** Optional extra classes — always append, never conflict with size/tone. */
  className?: string
  style?: CSSProperties
  as?: 'section' | 'div' | 'article' | 'aside'
  children: ReactNode
}

/** Border-radius per size — hero gets the bigger, premium-feeling radius. */
const RADIUS_CLASSES: Record<CardSize, string> = {
  hero: 'rounded-3xl',
  default: 'rounded-2xl',
  flat: 'rounded-2xl',
}

/** Default shadow per size, used when the tone doesn't bring its own. */
const SIZE_SHADOW_CLASSES: Record<CardSize, string> = {
  hero: 'shadow-brand-lg',
  default: 'shadow-brand',
  flat: '',
}

/**
 * Tone classes own the surface fill, the text colour, AND (for gradient)
 * the shadow — because the gradient hero needs the special premium shadow
 * with the inset top highlight, not the generic shadow-brand-lg. The
 * gradient deliberately has NO border: the inset highlight defines the
 * top edge as a "lifted glass" effect, and a real border on top of that
 * shows up as a visible 1px outline against the gradient.
 */
/**
 * Tone-specific surface classes.
 *
 * Gradient deliberately has NO border: the inset top highlight from the
 * `shadow-brand-hero` recipe defines the upper edge as a "lifted glass"
 * effect, and a real 1px border on top of that shows up as a visible
 * outline against the gradient — which is exactly the artifact users see
 * as "this looks cheap" on glossy cards.
 *
 * Soft tones keep their tinted borders because the soft fill is close in
 * lightness to the page background; without a border the card boundary
 * gets lost.
 */
const TONE_SURFACE_CLASSES: Record<CardTone, string> = {
  surface: 'bg-brand-surface border border-brand-border',
  gradient: 'bg-brand-gradient-hero text-brand-on-primary',
  'primary-soft': 'bg-brand-primary-soft border border-brand-border',
  'warning-soft': 'bg-brand-warning-soft border border-brand-warning/30',
  'error-soft': 'bg-brand-error-soft border border-brand-error/30',
  'info-soft': 'bg-brand-info-soft border border-brand-border',
}

/** Tones that override the size's default shadow. Currently just gradient. */
const TONE_SHADOW_OVERRIDE: Partial<Record<CardTone, string>> = {
  gradient: 'shadow-brand-hero',
}

export function Card({
  size = 'default',
  tone = 'surface',
  className = '',
  style,
  as: Tag = 'section',
  children,
  ...rest
}: CardProps) {
  const radius = RADIUS_CLASSES[size]
  // Tone shadow takes precedence so gradient cards get the premium recipe
  // regardless of size; everything else falls back to the size default.
  const shadow = TONE_SHADOW_OVERRIDE[tone] ?? SIZE_SHADOW_CLASSES[size]
  const surface = TONE_SURFACE_CLASSES[tone]
  return (
    <Tag
      className={`${radius} ${shadow} ${surface} ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </Tag>
  )
}
